import { base } from './db'
import { emettre } from './bus'
import { horlogeSysteme, type Horloge } from './horloge'
import type { EvenementFile, OpSync } from '../domaine/types'

/*
  File d'attente locale — le cœur du hors-ligne.

  Principe : toute action écrit d'abord l'entité locale, puis l'événement ici,
  puis rend l'interface. Aucune saisie n'attend jamais une réponse réseau.

  L'`id` de l'événement EST l'`id` de l'entité. C'est la clé d'idempotence :
  le serveur applique `ON CONFLICT (id) DO NOTHING`, donc un lot renvoyé dix
  fois n'insère jamais deux fois la même ligne.
*/

/** Paliers de backoff, en millisecondes. Le dernier est le plafond. */
export const PALIERS_BACKOFF = [2_000, 5_000, 15_000, 45_000, 120_000, 300_000] as const

export function delaiBackoff(essais: number): number {
  const i = Math.min(Math.max(essais, 0), PALIERS_BACKOFF.length - 1)
  return PALIERS_BACKOFF[i] as number
}

export const MAX_EVENEMENTS_PAR_LOT = 100
export const MAX_OCTETS_PAR_LOT = 1_000_000

/**
 * Ajoute un événement à la file. Retourne immédiatement : aucun appel réseau.
 *
 * @param id  L'identifiant de l'entité concernée, généré côté client.
 */
export async function enfiler(
  id: string,
  op: OpSync,
  donnees: Record<string, unknown>,
  horloge: Horloge = horlogeSysteme,
): Promise<EvenementFile> {
  const db = await base()
  const tx = db.transaction('file', 'readwrite')
  const magasin = tx.objectStore('file')

  // Rang monotone : les uuid sont aléatoires, donc sans ce compteur l'ordre
  // d'envoi serait celui des clés, c'est-à-dire arbitraire. On veut du FIFO.
  const dernier = await magasin.index('par-rang').openCursor(null, 'prev')
  const rang = (dernier?.value.rang ?? 0) + 1

  const evenement: EvenementFile = {
    id,
    op,
    donnees,
    cree_le: horloge.iso(),
    rang,
    essais: 0,
    prochain_essai_le: horloge.maintenant(),
    etat: 'en_attente',
  }

  await magasin.put(evenement)
  await tx.done

  emettre('file-changee')
  return evenement
}

/**
 * Prochain lot à envoyer, dans l'ordre d'insertion.
 *
 * Les photos partent SEULES : un lot de 800 Ko qui échoue à 95 % sur un réseau
 * de plage ne doit pas faire retomber quarante observations avec lui.
 */
export async function lotAEnvoyer(
  horloge: Horloge = horlogeSysteme,
  maxEvenements = MAX_EVENEMENTS_PAR_LOT,
  maxOctets = MAX_OCTETS_PAR_LOT,
): Promise<EvenementFile[]> {
  const db = await base()
  const maintenant = horloge.maintenant()

  const tous = await db.getAllFromIndex('file', 'par-rang')
  const eligibles = tous.filter(
    (e) => e.etat === 'en_attente' && e.prochain_essai_le <= maintenant,
  )
  if (eligibles.length === 0) return []

  const premier = eligibles[0] as EvenementFile
  if (premier.op === 'photo.televerser') return [premier]

  const lot: EvenementFile[] = []
  let octets = 0

  for (const e of eligibles) {
    if (e.op === 'photo.televerser') break // la photo ouvrira le lot suivant
    if (lot.length >= maxEvenements) break

    const taille = JSON.stringify(e.donnees).length
    if (lot.length > 0 && octets + taille > maxOctets) break

    lot.push(e)
    octets += taille
  }

  return lot
}

/** Purge les événements confirmés par le serveur (y compris les « déjà là »). */
export async function retirer(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const db = await base()
  const tx = db.transaction('file', 'readwrite')
  for (const id of ids) {
    await tx.objectStore('file').delete(id)
  }
  await tx.done
  emettre('file-changee')
}

/** Échec réseau : on réessaiera, avec un délai croissant plafonné. */
export async function reporter(
  ids: string[],
  horloge: Horloge = horlogeSysteme,
): Promise<void> {
  if (ids.length === 0) return
  const db = await base()
  const tx = db.transaction('file', 'readwrite')
  const magasin = tx.objectStore('file')

  for (const id of ids) {
    const e = await magasin.get(id)
    if (!e) continue
    const essais = e.essais + 1
    await magasin.put({
      ...e,
      essais,
      prochain_essai_le: horloge.maintenant() + delaiBackoff(essais - 1),
    })
  }
  await tx.done
  emettre('file-changee')
}

/**
 * Erreur de validation : l'événement est définitivement écarté de l'envoi.
 *
 * On ne le réessaie JAMAIS — il échouerait à l'infini et bloquerait la file
 * derrière lui. Il reste stocké et visible, pour ne pas perdre silencieusement
 * une saisie de terrain.
 */
export async function mettreEnQuarantaine(id: string, raison: string): Promise<void> {
  const db = await base()
  const e = await db.get('file', id)
  if (!e) return
  await db.put('file', { ...e, etat: 'quarantaine', raison })
  emettre('file-changee')
}

/** Réarme tout : appelé au retour de connectivité. */
export async function reveiller(horloge: Horloge = horlogeSysteme): Promise<void> {
  const db = await base()
  const tx = db.transaction('file', 'readwrite')
  const magasin = tx.objectStore('file')
  let curseur = await magasin.openCursor()
  const maintenant = horloge.maintenant()

  while (curseur) {
    const e = curseur.value
    if (e.etat === 'en_attente' && e.prochain_essai_le > maintenant) {
      await curseur.update({ ...e, prochain_essai_le: maintenant })
    }
    curseur = await curseur.continue()
  }
  await tx.done
  emettre('file-changee')
}

export interface EtatFile {
  enAttente: number
  enQuarantaine: number
  /** ISO du plus ancien élément non envoyé, pour afficher son ancienneté. */
  plusAncien: string | null
  /** Prochaine échéance de réessai, en epoch ms. `null` si rien à envoyer. */
  prochaineEcheance: number | null
}

export async function etatFile(): Promise<EtatFile> {
  const db = await base()
  const tous = await db.getAllFromIndex('file', 'par-rang')

  const attente = tous.filter((e) => e.etat === 'en_attente')
  const quarantaine = tous.filter((e) => e.etat === 'quarantaine')

  return {
    enAttente: attente.length,
    enQuarantaine: quarantaine.length,
    plusAncien: attente[0]?.cree_le ?? null,
    prochaineEcheance:
      attente.length > 0 ? Math.min(...attente.map((e) => e.prochain_essai_le)) : null,
  }
}
